/**
 * ExtractionTab — extraction runs + candidate review queue for one KB.
 *
 * Layout:
 *   ┌── sub-tabs: Runs | Topics | Predicates | Entities ────────────┐
 *   │ ┌──────────────────────────────────────────────────────────┐  │
 *   │ │ Runs:  [doc dropdown] → table of runs (status, tokens…) │  │
 *   │ │ Topics / Predicates / Entities:                          │  │
 *   │ │   [pending|promoted|rejected] toggle                     │  │
 *   │ │   table of candidates  → [promote] [reject] inline       │  │
 *   │ └──────────────────────────────────────────────────────────┘  │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Why a single component:
 *
 * - Runs and candidates share the KB id and the same "post-promote
 *   refresh" flow (a successful promote bumps both this tab's
 *   refreshKey *and* ``onPromoted`` so the Taxonomy tab sees the new
 *   row).
 * - Each candidate kind needs a slightly different promote payload
 *   (topics/predicates take a slug, entities take an entity_type_id),
 *   so we open a small inline dialog rather than a single one-size
 *   form. Reject takes an optional reason and reuses the same dialog
 *   for all three kinds.
 *
 * Data loading:
 *
 * - Each sub-tab loads on mount + on refreshKey bumps; switching
 *   sub-tabs is free (state is kept). The candidate tables don't
 *   poll — pending counts only change when the worker writes new
 *   rows, and the user has a refresh button. The runs table polls
 *   every 5 s while any row is queued/running.
 */

import { Loader2, Play, RefreshCw, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { EmptyState, ErrorBanner } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ApiError, api } from "@/lib/api";
import {
  type AnyCandidateRow,
  type CandidateRow,
  candidateLabel,
  getTaxonomy,
  listEntityCandidates,
  listExtractionRuns,
  listPredicateCandidates,
  listTopicCandidates,
  previewPrompt,
  promoteEntityCandidate,
  promotePredicateCandidate,
  promoteTopicCandidate,
  rejectEntityCandidate,
  rejectPredicateCandidate,
  rejectTopicCandidate,
} from "@/lib/kb-extraction";
import type {
  CandidateKind,
  CandidateStatus,
  ExtractorKind,
  KbDocumentListResponse,
  KbDocumentView,
  KbEntityCandidateView,
  KbEntityTypeView,
  KbExtractionRunView,
  KbPredicateCandidateView,
  KbPreviewResponse,
  KbTopicCandidateView,
  TaxonomySource,
} from "@/lib/types";
import { EXTRACTORS } from "@/lib/types";

interface ExtractionTabProps {
  kbId: string;
  /** Bumped from sibling tabs (e.g. Documents tab after a fresh
   *  ingest) so the candidate queue and runs table refresh without
   *  a remount. */
  refreshKey?: number;
  /** Called after a successful promote so the parent can refresh
   *  the Taxonomy tab. */
  onPromoted?: () => void;
}

type ExtractionSubTab = "runs" | "topics" | "predicates" | "entities" | "preview";

export function ExtractionTab({ kbId, refreshKey, onPromoted }: ExtractionTabProps) {
  const [activeSubTab, setActiveSubTab] = useState<ExtractionSubTab>("topics");

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Tabs
        value={activeSubTab}
        onValueChange={(v) => setActiveSubTab(v as ExtractionSubTab)}
        className="flex h-full min-h-0 flex-col"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
          <TabsList className="h-7 p-0.5">
            <TabsTrigger value="runs" className="h-6 px-3">
              Runs
            </TabsTrigger>
            <TabsTrigger value="topics" className="h-6 px-3">
              Topics
            </TabsTrigger>
            <TabsTrigger value="predicates" className="h-6 px-3">
              Predicates
            </TabsTrigger>
            <TabsTrigger value="entities" className="h-6 px-3">
              Entities
            </TabsTrigger>
            <TabsTrigger value="preview" className="h-6 px-3">
              Preview
            </TabsTrigger>
          </TabsList>
          <span className="text-[11px] text-muted-foreground">
            Pending candidates land here from extraction runs. Promote pushes them into the
            taxonomy; reject removes them from the queue.
          </span>
        </div>
        <TabsContent value="runs" className="mt-0 min-h-0 flex-1 overflow-auto">
          <RunsPanel kbId={kbId} refreshKey={refreshKey ?? 0} />
        </TabsContent>
        <TabsContent value="topics" className="mt-0 min-h-0 flex-1 overflow-auto">
          <CandidatesPanel
            kbId={kbId}
            kind="topics"
            refreshKey={refreshKey ?? 0}
            onPromoted={onPromoted}
          />
        </TabsContent>
        <TabsContent value="predicates" className="mt-0 min-h-0 flex-1 overflow-auto">
          <CandidatesPanel
            kbId={kbId}
            kind="predicates"
            refreshKey={refreshKey ?? 0}
            onPromoted={onPromoted}
          />
        </TabsContent>
        <TabsContent value="entities" className="mt-0 min-h-0 flex-1 overflow-auto">
          <CandidatesPanel
            kbId={kbId}
            kind="entities"
            refreshKey={refreshKey ?? 0}
            onPromoted={onPromoted}
          />
        </TabsContent>
        <TabsContent value="preview" className="mt-0 min-h-0 flex-1 overflow-auto">
          <PromptPreviewPanel kbId={kbId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Runs sub-tab
// ────────────────────────────────────────────────────────────────────

function RunsPanel({ kbId, refreshKey }: { kbId: string; refreshKey: number }) {
  const [docs, setDocs] = useState<KbDocumentView[] | null>(null);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [runs, setRuns] = useState<KbExtractionRunView[] | null>(null);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState<string | null>(null);

  const loadDocs = useCallback(async () => {
    setDocsError(null);
    try {
      // Reuse the documents-list endpoint (same as the Documents
      // tab uses). 200 docs is a reasonable cap; KBs that exceed
      // that need server-side pagination, which we'll add when we
      // see it in the wild.
      const resp = await api<KbDocumentListResponse>(`/kbs/${kbId}/documents?limit=200`);
      setDocs(resp.items);
      // Use the functional setter so we don't have to depend on
      // ``selectedDocId`` here — keeping it out of the dep array
      // prevents an extra refetch round-trip after the auto-pick.
      setSelectedDocId((current) =>
        current === null && resp.items.length > 0 ? resp.items[0].id : current,
      );
    } catch (err) {
      setDocsError(err instanceof Error ? err.message : "Failed to load documents");
    }
  }, [kbId]);

  const loadRuns = useCallback(async () => {
    if (!selectedDocId) {
      setRuns(null);
      return;
    }
    setRunsLoading(true);
    setRunsError(null);
    try {
      const items = await listExtractionRuns(kbId, {
        document_id: selectedDocId,
        limit: 50,
      });
      setRuns(items);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.status} ${err.message}`
          : err instanceof Error
            ? err.message
            : "Failed to load runs";
      setRunsError(msg);
      setRuns(null);
    } finally {
      setRunsLoading(false);
    }
  }, [kbId, selectedDocId]);

  useEffect(() => {
    void refreshKey;
    void loadDocs();
  }, [loadDocs, refreshKey]);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  // Auto-poll while any run is in flight; mirrors the Documents tab
  // pattern. We don't want a chatty 1 s poll because LLM runs can
  // take 30+ s and the cost of being stale for 5 s is nil.
  useEffect(() => {
    if (!runs) return;
    const inFlight = runs.some((r) => r.status === "queued" || r.status === "running");
    if (!inFlight) return;
    const id = window.setInterval(() => void loadRuns(), 5000);
    return () => window.clearInterval(id);
  }, [runs, loadRuns]);

  const selectedDoc = useMemo(
    () => docs?.find((d) => d.id === selectedDocId) ?? null,
    [docs, selectedDocId],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Label htmlFor="run-doc-picker" className="text-xs text-muted-foreground">
          Document
        </Label>
        <Select
          value={selectedDocId ?? ""}
          onValueChange={(v) => setSelectedDocId(v || null)}
          disabled={!docs || docs.length === 0}
        >
          <SelectTrigger id="run-doc-picker" className="h-7 w-72 text-xs">
            <SelectValue placeholder="Pick a document…" />
          </SelectTrigger>
          <SelectContent>
            {docs?.map((d) => (
              <SelectItem key={d.id} value={d.id} className="text-xs">
                {d.title || d.source_uri.split("/").slice(-1)[0]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto h-6 px-2 text-xs"
          onClick={() => void loadRuns()}
          disabled={!selectedDocId || runsLoading}
          aria-label="Refresh runs"
        >
          <RefreshCw className={runsLoading ? "animate-spin" : undefined} />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {docsError ? (
          <ErrorBanner className="m-3">{docsError}</ErrorBanner>
        ) : !docs ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading documents…
          </div>
        ) : docs.length === 0 ? (
          <EmptyState className="m-3">No documents in this KB yet.</EmptyState>
        ) : runsError ? (
          <ErrorBanner className="m-3">{runsError}</ErrorBanner>
        ) : runsLoading && !runs ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading runs…
          </div>
        ) : runs && runs.length > 0 ? (
          <RunsTable runs={runs} />
        ) : selectedDoc ? (
          <EmptyState className="m-3">
            No extraction runs for{" "}
            <span className="font-mono">{selectedDoc.title || "this document"}</span> yet.
          </EmptyState>
        ) : (
          <EmptyState className="m-3">Pick a document to see its extraction runs.</EmptyState>
        )}
      </div>
    </div>
  );
}

function RunsTable({ runs }: { runs: KbExtractionRunView[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-32 text-xs">Extractor</TableHead>
          <TableHead className="w-24 text-xs">Status</TableHead>
          <TableHead className="w-40 text-xs">Started</TableHead>
          <TableHead className="hidden w-24 text-xs md:table-cell">Chunks</TableHead>
          <TableHead className="hidden w-40 text-xs md:table-cell">Tokens (in / out)</TableHead>
          <TableHead className="hidden w-24 text-xs md:table-cell">Cost (USD)</TableHead>
          <TableHead className="hidden text-xs md:table-cell">Error</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {runs.map((r) => (
          <TableRow key={r.id}>
            <TableCell className="text-xs font-mono">
              {r.extractor}
              <div className="text-[10px] text-muted-foreground">
                {r.extractor_version} · {r.provider ?? "?"}/{r.model ?? "?"}
              </div>
            </TableCell>
            <TableCell>
              <RunStatusBadge status={r.status} />
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {r.started_at ? formatTimestamp(r.started_at) : "—"}
            </TableCell>
            <TableCell className="hidden text-xs md:table-cell">
              {r.chunks_processed}
              {r.chunks_failed > 0 ? (
                <span className="ml-1 text-destructive">/ {r.chunks_failed} failed</span>
              ) : null}
            </TableCell>
            <TableCell className="hidden text-xs md:table-cell">
              {r.total_input_tokens.toLocaleString()} / {r.total_output_tokens.toLocaleString()}
            </TableCell>
            <TableCell className="hidden text-xs md:table-cell">
              {r.total_cost_usd > 0 ? `$${r.total_cost_usd.toFixed(4)}` : "—"}
            </TableCell>
            <TableCell className="hidden truncate text-xs text-destructive md:table-cell">
              {r.error_message || ""}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function RunStatusBadge({ status }: { status: KbExtractionRunView["status"] }) {
  if (status === "succeeded")
    return (
      <Badge variant="default" className="text-[10px]">
        succeeded
      </Badge>
    );
  if (status === "running" || status === "queued")
    return (
      <Badge variant="secondary" className="text-[10px]">
        {status}
      </Badge>
    );
  if (status === "failed" || status === "cancelled")
    return (
      <Badge variant="destructive" className="text-[10px]">
        {status}
      </Badge>
    );
  return (
    <Badge variant="outline" className="text-[10px]">
      {status}
    </Badge>
  );
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ────────────────────────────────────────────────────────────────────
// Candidates sub-tabs (topics / predicates / entities)
// ────────────────────────────────────────────────────────────────────

interface CandidatesPanelProps {
  kbId: string;
  kind: CandidateKind;
  refreshKey: number;
  onPromoted?: () => void;
}

function CandidatesPanel({ kbId, kind, refreshKey, onPromoted }: CandidatesPanelProps) {
  const [status, setStatus] = useState<CandidateStatus>("pending");
  const [rows, setRows] = useState<AnyCandidateRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cache of entity types so the entity-promote dialog can offer a
  // dropdown rather than asking the user to paste a UUID. We
  // refetch on refreshKey because new entity types might have been
  // added via the Taxonomy tab.
  const [entityTypes, setEntityTypes] = useState<KbEntityTypeView[]>([]);

  const [promoteFor, setPromoteFor] = useState<CandidateRow | null>(null);
  const [rejectFor, setRejectFor] = useState<CandidateRow | null>(null);

  const refresh = useCallback(async () => {
    void refreshKey;
    setLoading(true);
    setError(null);
    try {
      let items: AnyCandidateRow[];
      if (kind === "topics") {
        items = await listTopicCandidates(kbId, { status, limit: 200 });
      } else if (kind === "predicates") {
        items = await listPredicateCandidates(kbId, { status, limit: 200 });
      } else {
        items = await listEntityCandidates(kbId, { status, limit: 200 });
      }
      setRows(items);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.status} ${err.message}`
          : err instanceof Error
            ? err.message
            : "Failed to load candidates";
      setError(msg);
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, [kbId, kind, status, refreshKey]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Lazy-load entity types only on the entities sub-tab. We touch
  // ``refreshKey`` explicitly so a parent bump (e.g. after a fresh
  // taxonomy edit) re-fetches the entity-type list, which the
  // promote dialog relies on. biome's exhaustive-deps doesn't follow
  // the ``void`` pattern so we keep the read here.
  useEffect(() => {
    void refreshKey;
    if (kind !== "entities") return;
    let cancelled = false;
    void getTaxonomy(kbId).then(
      (b) => {
        if (!cancelled) setEntityTypes(b.entity_types);
      },
      () => {
        // Failure here just means the promote dialog will say "no
        // entity types" — not fatal, the user can promote later.
      },
    );
    return () => {
      cancelled = true;
    };
  }, [kbId, kind, refreshKey]);

  const handlePromoted = useCallback(() => {
    setPromoteFor(null);
    void refresh();
    onPromoted?.();
  }, [refresh, onPromoted]);

  const handleRejected = useCallback(() => {
    setRejectFor(null);
    void refresh();
  }, [refresh]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Label htmlFor={`cand-status-${kind}`} className="text-xs text-muted-foreground">
          Status
        </Label>
        <Select value={status} onValueChange={(v) => setStatus(v as CandidateStatus)}>
          <SelectTrigger id={`cand-status-${kind}`} className="h-7 w-32 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending" className="text-xs">
              pending
            </SelectItem>
            <SelectItem value="promoted" className="text-xs">
              promoted
            </SelectItem>
            <SelectItem value="rejected" className="text-xs">
              rejected
            </SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-2 text-[11px] text-muted-foreground">
          {rows ? `${rows.length} candidate${rows.length === 1 ? "" : "s"}` : ""}
        </span>
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto h-6 px-2 text-xs"
          onClick={() => void refresh()}
          disabled={loading}
          aria-label="Refresh candidates"
        >
          <RefreshCw className={loading ? "animate-spin" : undefined} />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {error ? (
          <ErrorBanner className="m-3">{error}</ErrorBanner>
        ) : loading && !rows ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading candidates…
          </div>
        ) : rows && rows.length > 0 ? (
          <CandidatesTable
            kind={kind}
            rows={rows}
            status={status}
            onPromote={(row) => setPromoteFor({ kind, row } as CandidateRow)}
            onReject={(row) => setRejectFor({ kind, row } as CandidateRow)}
          />
        ) : (
          <EmptyState className="m-3">
            <Sparkles className="h-4 w-4" />
            <span>
              No {status} {kind} yet.
            </span>
          </EmptyState>
        )}
      </div>
      {promoteFor ? (
        <PromoteDialog
          kbId={kbId}
          target={promoteFor}
          entityTypes={entityTypes}
          onClose={() => setPromoteFor(null)}
          onSuccess={handlePromoted}
        />
      ) : null}
      {rejectFor ? (
        <RejectDialog
          kbId={kbId}
          target={rejectFor}
          onClose={() => setRejectFor(null)}
          onSuccess={handleRejected}
        />
      ) : null}
    </div>
  );
}

function CandidatesTable({
  kind,
  rows,
  status,
  onPromote,
  onReject,
}: {
  kind: CandidateKind;
  rows: AnyCandidateRow[];
  status: CandidateStatus;
  onPromote: (row: AnyCandidateRow) => void;
  onReject: (row: AnyCandidateRow) => void;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-xs">
            {kind === "entities" ? "Canonical name" : "Label"}
          </TableHead>
          <TableHead className="hidden text-xs md:table-cell">
            {kind === "entities" ? "Aliases / type hint" : "Description"}
          </TableHead>
          <TableHead className="w-20 text-xs">Conf.</TableHead>
          <TableHead className="w-16 text-xs">×</TableHead>
          <TableHead className="w-24 text-xs">Source</TableHead>
          <TableHead className="w-44 text-xs">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell className="text-xs font-medium">{candidateLabel(kind, row)}</TableCell>
            <TableCell className="hidden text-xs text-muted-foreground md:table-cell">
              {kind === "entities"
                ? renderEntityHint(row as KbEntityCandidateView)
                : (row as KbTopicCandidateView | KbPredicateCandidateView).proposed_description ||
                  "—"}
            </TableCell>
            <TableCell className="text-xs">{row.confidence.toFixed(2)}</TableCell>
            <TableCell className="text-xs">{row.occurrences}</TableCell>
            <TableCell>
              <SourceBadge source={row.source} />
            </TableCell>
            <TableCell>
              {status === "pending" ? (
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="default"
                    className="h-6 px-2 text-xs"
                    onClick={() => onPromote(row)}
                  >
                    Promote
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={() => onReject(row)}
                  >
                    Reject
                  </Button>
                </div>
              ) : (
                <span className="text-[11px] text-muted-foreground">{status}</span>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function renderEntityHint(row: KbEntityCandidateView): string {
  const parts: string[] = [];
  if (row.proposed_aliases.length > 0) {
    parts.push(`aliases: ${row.proposed_aliases.slice(0, 3).join(", ")}`);
    if (row.proposed_aliases.length > 3) parts.push(`+${row.proposed_aliases.length - 3} more`);
  }
  if (row.identifier_type && row.identifier_value) {
    parts.push(`${row.identifier_type}: ${row.identifier_value}`);
  }
  if (row.market) parts.push(`market: ${row.market}`);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

function SourceBadge({ source }: { source: TaxonomySource }) {
  if (source === "ai")
    return (
      <Badge variant="secondary" className="text-[10px]">
        ai
      </Badge>
    );
  if (source === "user")
    return (
      <Badge variant="default" className="text-[10px]">
        user
      </Badge>
    );
  if (source === "system")
    return (
      <Badge variant="outline" className="text-[10px]">
        system
      </Badge>
    );
  return (
    <Badge variant="outline" className="text-[10px]">
      {source}
    </Badge>
  );
}

// ────────────────────────────────────────────────────────────────────
// Promote / Reject dialogs (shared across all three kinds)
// ────────────────────────────────────────────────────────────────────

const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

function suggestSlug(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

function PromoteDialog({
  kbId,
  target,
  entityTypes,
  onClose,
  onSuccess,
}: {
  kbId: string;
  target: CandidateRow;
  entityTypes: KbEntityTypeView[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const initialSlug = useMemo(
    () => (target.kind === "entities" ? "" : suggestSlug(candidateLabel(target.kind, target.row))),
    [target],
  );
  const [slug, setSlug] = useState(initialSlug);
  const [entityTypeId, setEntityTypeId] = useState<string>(entityTypes[0]?.id ?? "");
  const [submitting, setSubmitting] = useState(false);

  const slugValid = SLUG_RE.test(slug);
  const valid = target.kind === "entities" ? Boolean(entityTypeId) : slugValid && slug.length > 0;

  async function handleSubmit() {
    setSubmitting(true);
    try {
      if (target.kind === "topics") {
        await promoteTopicCandidate(kbId, target.row.id, { slug });
      } else if (target.kind === "predicates") {
        await promotePredicateCandidate(kbId, target.row.id, { slug });
      } else {
        await promoteEntityCandidate(kbId, target.row.id, { entity_type_id: entityTypeId });
      }
      toast.success(`Promoted ${target.kind.slice(0, -1)} candidate`);
      onSuccess();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.status} ${err.message}`
          : err instanceof Error
            ? err.message
            : "Promote failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Promote {target.kind.slice(0, -1)}</DialogTitle>
          <DialogDescription>
            Move <span className="font-medium">{candidateLabel(target.kind, target.row)}</span> into
            the catalog. This is reversible — you can delete the resulting row from the Taxonomy
            tab.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {target.kind === "entities" ? (
            <div className="space-y-1">
              <Label htmlFor="promote-entity-type">Entity type</Label>
              <Select value={entityTypeId} onValueChange={setEntityTypeId}>
                <SelectTrigger id="promote-entity-type">
                  <SelectValue placeholder="Pick an entity type…" />
                </SelectTrigger>
                <SelectContent>
                  {entityTypes.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      No entity types declared. Add one from the Taxonomy tab first.
                    </div>
                  ) : (
                    entityTypes.map((t) => (
                      <SelectItem key={t.id} value={t.id} className="text-xs">
                        {t.name} <span className="text-muted-foreground">({t.slug})</span>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-1">
              <Label htmlFor="promote-slug">Slug</Label>
              <Input
                id="promote-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="lowercase-with-dashes"
                autoFocus
              />
              <p className="text-[11px] text-muted-foreground">
                Must match <code>[a-z0-9][a-z0-9-]*</code>. Used to dedupe across re-runs and
                channels.
              </p>
              {!slugValid && slug.length > 0 ? (
                <p className="text-[11px] text-destructive">Slug format is invalid.</p>
              ) : null}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!valid || submitting}>
            {submitting ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
            Promote
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({
  kbId,
  target,
  onClose,
  onSuccess,
}: {
  kbId: string;
  target: CandidateRow;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const body = reason.trim() ? { rejection_reason: reason.trim() } : {};
      if (target.kind === "topics") {
        await rejectTopicCandidate(kbId, target.row.id, body);
      } else if (target.kind === "predicates") {
        await rejectPredicateCandidate(kbId, target.row.id, body);
      } else {
        await rejectEntityCandidate(kbId, target.row.id, body);
      }
      toast.success(`Rejected ${target.kind.slice(0, -1)} candidate`);
      onSuccess();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.status} ${err.message}`
          : err instanceof Error
            ? err.message
            : "Reject failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reject {target.kind.slice(0, -1)}</DialogTitle>
          <DialogDescription>
            Reject <span className="font-medium">{candidateLabel(target.kind, target.row)}</span>.
            We keep the row (with status=rejected) so the worker doesn't keep proposing it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label htmlFor="reject-reason">Reason (optional)</Label>
          <Input
            id="reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. duplicates an existing topic"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
            Reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// Prompt preview sub-tab — pure prompt builder, no LLM call
// ────────────────────────────────────────────────────────────────────

const PREVIEW_DEFAULT_CONTENT = `# Sample chunk

The Federal Reserve raised interest rates by 25 basis points to a target range
of 5.25%–5.50% in July 2023, citing persistent inflation pressures. Powell
indicated the FOMC would remain data-dependent for the September meeting.`;

function PromptPreviewPanel({ kbId }: { kbId: string }) {
  // Local form state — kept simple. We don't auto-debounce-submit
  // because rendering a prompt is cheap server-side but the
  // Responses API metadata view + scroll position are jarring if it
  // updates on every keystroke. Click-to-render is the whole point
  // of "preview without spending tokens".
  const [extractor, setExtractor] = useState<ExtractorKind>("topic");
  const [content, setContent] = useState(PREVIEW_DEFAULT_CONTENT);
  const [seqStr, setSeqStr] = useState<string>("0");
  const [chunkId, setChunkId] = useState("");
  const [metricHintsStr, setMetricHintsStr] = useState("");
  const [response, setResponse] = useState<KbPreviewResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const seq = seqStr.trim() ? Number(seqStr.trim()) : undefined;
      if (seq !== undefined && !Number.isFinite(seq)) {
        throw new Error("seq must be an integer");
      }
      const metric_hints = metricHintsStr
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      const r = await previewPrompt(kbId, {
        extractor,
        content,
        seq,
        chunk_id: chunkId.trim() || undefined,
        metric_hints: metric_hints.length > 0 ? metric_hints : undefined,
      });
      setResponse(r);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.status} ${err.message}`
          : err instanceof Error
            ? err.message
            : "Preview failed";
      setError(msg);
      setResponse(null);
    } finally {
      setSubmitting(false);
    }
  }

  const valid = content.trim().length > 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="space-y-3 border-b border-border bg-muted/30 px-3 py-3 text-xs">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="preview-extractor">Extractor</Label>
            <Select value={extractor} onValueChange={(v) => setExtractor(v as ExtractorKind)}>
              <SelectTrigger id="preview-extractor" className="h-8 w-44 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXTRACTORS.map((e) => (
                  <SelectItem key={e} value={e} className="text-xs">
                    {e}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="preview-seq">Seq</Label>
            <Input
              id="preview-seq"
              value={seqStr}
              onChange={(e) => setSeqStr(e.target.value)}
              className="h-8 w-20"
              placeholder="0"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="preview-chunk-id">Chunk id (optional)</Label>
            <Input
              id="preview-chunk-id"
              value={chunkId}
              onChange={(e) => setChunkId(e.target.value)}
              className="h-8 w-72 font-mono text-[11px]"
              placeholder="chunk_…"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="preview-metric-hints">Metric hints (comma-separated)</Label>
            <Input
              id="preview-metric-hints"
              value={metricHintsStr}
              onChange={(e) => setMetricHintsStr(e.target.value)}
              className="h-8 w-72"
              placeholder="revenue, eps, fed_funds_rate"
              disabled={extractor !== "claim_fact"}
            />
          </div>
          <Button
            onClick={() => void handleSubmit()}
            disabled={!valid || submitting}
            className="h-8"
          >
            {submitting ? (
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
            ) : (
              <Play className="mr-2 h-3 w-3" />
            )}
            Render prompt
          </Button>
        </div>
        <div className="space-y-1">
          <Label htmlFor="preview-content">Chunk content</Label>
          <Textarea
            id="preview-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={6}
            className="font-mono text-[11px]"
            placeholder="Paste a chunk of text the extractor would see…"
          />
          <p className="text-[11px] text-muted-foreground">
            No LLM call is made and no run row is recorded — this is a pure render of the prompt the
            extractor would build for this chunk.
          </p>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {error ? (
          <ErrorBanner>{error}</ErrorBanner>
        ) : response ? (
          <PreviewMessages response={response} />
        ) : (
          <EmptyState>
            <Sparkles className="h-4 w-4" />
            <span>Click "Render prompt" to see the rendered system + user messages.</span>
          </EmptyState>
        )}
      </div>
    </div>
  );
}

function PreviewMessages({ response }: { response: KbPreviewResponse }) {
  return (
    <div className="space-y-3">
      <PreviewMessage label="System" body={response.system} />
      <PreviewMessage label="User" body={response.user} />
    </div>
  );
}

function PreviewMessage({ label, body }: { label: string; body: string }) {
  return (
    <div className="rounded border border-border">
      <div className="flex items-center justify-between border-b border-border bg-muted/40 px-2 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">
        <span>{label}</span>
        <Button
          size="sm"
          variant="ghost"
          className="h-5 px-2 text-[10px]"
          onClick={() => {
            void navigator.clipboard.writeText(body).then(
              () => toast.success(`${label} copied`),
              () => toast.error("Copy failed"),
            );
          }}
        >
          Copy
        </Button>
      </div>
      <pre className="whitespace-pre-wrap break-words p-2 font-mono text-[11px] leading-snug">
        {body}
      </pre>
    </div>
  );
}
